// Public ledger cost estimates (stage 9), as public-ledger-estimate answers them, and how they read. Pure, no DOM.
// Estimates are in nodeStream credits only (OQ8). Ledger fees are pegged to the US dollar, so an estimate in credits moves
// with the HBAR price: the server converts at the current rate each time, and these figures are never stored.

import { formatCredits } from './public-ledger-credits.js';

export type LedgerPayer = 'COMPANY' | 'PLATFORM';

export interface CreditRange {
  min: number;
  max: number;
}

export interface LedgerEstimateItem {
  nodeId: string | null;
  title: string | null;
  kind: string;
  payer: LedgerPayer;
  credits: CreditRange;
}

export interface LedgerEstimate {
  // Everything, whoever pays
  total: CreditRange;
  company: CreditRange;
  platform: CreditRange;
  items: LedgerEstimateItem[];
  // Whether the operating account's reading covers the maximum plus the margin; null when the server didn't say
  covers: 'YES' | 'NO' | null;
  // A signatory change whose new rule isn't known yet, so its maximum isn't either
  openEnded: boolean;
  // Too many paths to walk: the figures are a lower bound
  truncated: boolean;
}

const EMPTY: CreditRange = { min: 0, max: 0 };

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const number = (value: unknown): number | null => {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : null;
};

// A range from {min, max}, or a single figure
function range(value: unknown): CreditRange | null {
  const single = number(value);
  if (single !== null) return { min: single, max: single };
  if (!isRecord(value)) return null;
  const min = number(value.min);
  const max = number(value.max);
  if (min === null && max === null) return null;
  const low = min ?? max ?? 0;
  return { min: low, max: Math.max(low, max ?? low) };
}

const sum = (ranges: CreditRange[]): CreditRange =>
  ranges.reduce((acc, r) => ({ min: acc.min + r.min, max: acc.max + r.max }), { ...EMPTY });

function item(value: unknown): LedgerEstimateItem | null {
  if (!isRecord(value)) return null;
  const credits = range(value.credits);
  if (!credits || typeof value.kind !== 'string') return null;
  return {
    nodeId: typeof value.nodeId === 'string' ? value.nodeId : null,
    title: typeof value.title === 'string' && value.title ? value.title : null,
    kind: value.kind,
    payer: value.payer === 'PLATFORM' ? 'PLATFORM' : 'COMPANY',
    credits,
  };
}

// What each kind of public ledger work is called on the item list when the line has no job title
const KIND_LABELS: Record<string, string> = {
  COMPANY_TOPIC_MESSAGE: 'Company public ledger message',
  INDIVIDUAL_TOPIC_MESSAGE: 'Personal public ledger message',
  INTERNAL_MESSAGE: 'nodeStream public ledger record',
  ACCOUNT_CREATION: 'Public ledger account',
  SIGNATORY_CHANGE: 'Change of signatories',
  TOPIC_SIGNATORY_CHANGE: 'Change of topic signatories',
  CREDIT_DELIVERY: 'Credit delivery',
};

/** An estimate line's name on the item list: its job's title, else what its kind of public ledger work is called. */
export function ledgerItemLabel(item: Pick<LedgerEstimateItem, 'title' | 'kind'>): string {
  return item.title ?? KIND_LABELS[item.kind] ?? 'Public ledger work';
}

/**
 * The estimate in a public-ledger-estimate answer, or null when it isn't one. The total is `min`/`max`, the per-item lines
 * are `lines`, and the split is `company` / `platform` (worked out from the lines when the answer has none).
 */
export function parseLedgerEstimate(raw: unknown): LedgerEstimate | null {
  if (!isRecord(raw)) return null;
  const lines = Array.isArray(raw.lines) ? raw.lines : Array.isArray(raw.items) ? raw.items : [];
  const items = lines.map(item).filter((i): i is LedgerEstimateItem => i !== null);
  const total = range({ min: raw.min, max: raw.max }) ?? sum(items.map((i) => i.credits));
  const totals = isRecord(raw.totals) ? raw.totals : raw;
  const company = range(totals.company) ?? sum(items.filter((i) => i.payer === 'COMPANY').map((i) => i.credits));
  const platform = range(totals.platform) ?? sum(items.filter((i) => i.payer === 'PLATFORM').map((i) => i.credits));
  return {
    total,
    company,
    platform,
    items,
    covers: raw.covers === 'YES' || raw.covers === 'NO' ? raw.covers : null,
    openEnded: raw.openEnded === true,
    truncated: raw.truncated === true,
  };
}

/** Whole credits as shown: anything above nothing and under one credit shows as 1 (OQ9), the rest rounds up. */
export function shownCredits(credits: number): number {
  if (!(credits > 0)) return 0;
  return Math.max(1, Math.ceil(credits - 1e-9));
}

// "1 credit", "32 credits"
const credits = (n: number) => `${formatCredits(n)} ${n === 1 ? 'credit' : 'credits'}`;

/** "11 credits", or "about 11–15 credits" when the cheapest and dearest ways through differ. */
export function formatCreditRange(value: CreditRange, { openEnded = false } = {}): string {
  const min = shownCredits(value.min);
  const max = shownCredits(value.max);
  if (openEnded) return `at least ${credits(min)}`;
  if (min === max) return credits(min);
  return `about ${formatCredits(min)}–${credits(max)}`;
}

/** Whether an estimate is worth showing: something on the public ledger, whoever pays. */
export function hasLedgerCost(estimate: LedgerEstimate | null | undefined): estimate is LedgerEstimate {
  return Boolean(estimate && estimate.total.max > 0);
}

/** "Estimated 32 credits · 11 remaining", or "Estimated 32 credits" with nothing left to estimate. */
export function formatEstimateLine(total: LedgerEstimate, remaining?: LedgerEstimate | null): string {
  const line = `Estimated ${formatCreditRange(total.total, { openEnded: total.openEnded })}`;
  if (!remaining) return line;
  const left = remaining.total.max > 0 ? formatCreditRange(remaining.total, { openEnded: remaining.openEnded }) : '0';
  return `${line} · ${left.replace(/ credits?$/, '')} remaining`;
}

/** Who pays, as separate lines: "32 credits paid by your company", "57 credits paid by nodeStream" (OQ10). */
export function formatPayerSplit(estimate: LedgerEstimate): string[] {
  const lines: string[] = [];
  if (estimate.company.max > 0) {
    lines.push(`${formatCreditRange(estimate.company, { openEnded: estimate.openEnded })} paid by your company`);
  }
  if (estimate.platform.max > 0) lines.push(`${formatCreditRange(estimate.platform)} paid by nodeStream`);
  return lines;
}

/** The job card's pill: "~32 credits", "~11–15 credits". */
export function formatEstimatePill(estimate: LedgerEstimate): string {
  return `~${formatCreditRange(estimate.total, { openEnded: estimate.openEnded }).replace(/^about /, '')}`;
}
